import { EndpointErrorCode } from '@dtos/endpoint-error';
import {
    createAuthenticationStore,
    createAuthorisationStore
} from '@services/factories';
import axios, {
    AxiosError,
    AxiosRequestConfig,
    AxiosResponse
} from 'axios';
import HttpService from '../HttpService';

let authorizationRequestInterceptorId: number | null = null;
let authorizationResponseInterceptorId: number | null = null;
let lastAuthorisationToken: string | null = null;

/**
 * @private Exposed for testing purposes only
 */
export function clearAuthorizationInterceptor() {
    if (authorizationRequestInterceptorId !== null) {
        axios.interceptors.request.eject(authorizationRequestInterceptorId);
    }

    if (authorizationResponseInterceptorId !== null) {
        axios.interceptors.response.eject(authorizationResponseInterceptorId);
    }

    authorizationResponseInterceptorId = null;
    authorizationRequestInterceptorId = null;
    lastAuthorisationToken = null;
}

/**
 * @private Exposed for testing purposes only
 * @param token - Bearer token to apply as Authorization
 */
export function replaceAuthorizationInterceptor(token: string) {
    if (lastAuthorisationToken === token) {
        return;
    }

    clearAuthorizationInterceptor();

    authorizationRequestInterceptorId = axios.interceptors.request.use(
        (requestConfig: AxiosRequestConfig) => ({
            ...requestConfig,
            headers: {
                ...requestConfig.headers,
                Authorization: `Bearer ${token}`
            }
        })
    );
    authorizationResponseInterceptorId = axios.interceptors.response.use(
        response => response,
        (error: AxiosError) => {
            if (error.response?.status === 403 &&
                error.response?.data.code === EndpointErrorCode.AuthTokenExpired) {
                createAuthenticationStore().expireAuthentication();
            }
            return Promise.reject(error);
        }
    );

    lastAuthorisationToken = token;
}

/**
 * Decorator: When applied over an endpoint method, it automagically requests a new guest token
 * and applies it before calling that method
 * @param target - Class Prototype
 * @param propertyKey - Method name being decorated
 * @param descriptor - Descriptor of the method property
 * @throws If the guest token request fails
 * @example
 * ```typescript
 * class SomeApiService {
 *     @RequireGuestToken
 *     async doStuffAsGuest(): Promise<void> {
 *         ...
 *     }
 * }
 * ```
 */
export function RequireGuestToken(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const httpService = new HttpService();
    const method: (...args: any[]) => any = descriptor.value;

    // tslint:disable-next-line:only-arrow-functions
    descriptor.value = async function (...args: any[]) {
        const response: AxiosResponse<{
            token: string;
        }> = await httpService.getGuestToken();

        replaceAuthorizationInterceptor(response.data.token);

        return method.apply(this, args);
    };
}

export enum RequireAuthenticationTokenErrors {
    MissingAuthToken = 'Authorisation token needs to be accepted',
    InvalidAuthToken = 'Authorisation token needs to be renewed'
}

/**
 * Decorator: When applied over an endpoint method, it will grab the authentication token from the store
 * and apply it before calling that method
 * @param target - Class Prototype
 * @param propertyKey - Method name being decorated
 * @param descriptor - Descriptor of the method property
 * * @throws If the authorisation token is missing or invalid
 * @example
 * ```typescript
 * class SomeApiService {
 *     @RequireAuthenticationToken
 *     async doStuffAsUser(): Promise<void> {
 *         ...
 *     }
 * }
 * ```
 */
export function RequireAuthenticationToken(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const method: (...args: any[]) => any = descriptor.value;

    descriptor.value = async function (...args: any[]) {
        // TODO: get authToken from authorisation store properly
        const authorisationStore = createAuthorisationStore();
        const authToken = authorisationStore?.authorisationToken;
        if (!authToken) {
            throw new Error(RequireAuthenticationTokenErrors.MissingAuthToken);
        }

        if (!authorisationStore?.isTokenValid) {
            throw new Error(RequireAuthenticationTokenErrors.InvalidAuthToken);
        }

        replaceAuthorizationInterceptor(authToken);

        return method.apply(this, args);
    };
}



// WEBPACK FOOTER //
// ./src/services/http/authorisation-interceptor.ts